/* eslint-disable @typescript-eslint/no-explicit-any -- handlers formulaire U2000 */
import { useMemo } from "react";
import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputCalendarCustom from "../../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import U2000InputCurrency from "../../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000InputDecimal from "../../../../u2000Core/components/formControls/input/U2000InputDecimal";
import U2000Label from "../../../../u2000Core/components/formControls/label/U2000Label";
import U2000InvalidFeedback from "../../../../u2000Core/components/formControls/feedback/U2000InvalidFeedback";
import U2000CheckBox from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000Panel from "../../../../u2000Core/components/layout/U2000Panel";
import type { TFunction } from "i18next";
import { NS } from "../../../U3134";
import { U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import {
  U3134_HONORARIUM_ENTRY_KIND,
  U3134_TRANSACTION_KIND,
  isU3134HonorariumFixedPriceLine,
  isU3134InvoicedLine,
  isU3134ProjectForfaitSellingLine,
  type U3134HonorariumEntryKind,
  type U3134WorkInProgressFormRow,
} from "../../../U3134Service";
import { u3125FormatInvoiceNumberDisplay } from "../../../../u3125Facture/U3125Service";
interface U3134WorkInProgressBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3134WorkInProgressFormRow>;
  t: TFunction;
}

/** Label → champ : aligné sur U2000InputText / U2000InputDecimal (`space-y-1.5`). */
const U3134_BODY_LABEL_FIELD_GAP = "space-y-1.5";

/**
 * Mobile : libellé + champ comme U2000 (space-y-1.5).
 * Desktop : libellés en ligne 1, champs ligne 2 — `md:space-y-0` pour ne pas doubler l’espacement sous le libellé (déjà `mb-1.5`).
 */
const U3134_BODY_LABEL_FIELD_MOBILE_GAP_MD_COMPACT = "flex flex-col space-y-1.5 md:space-y-0";

/** Même champ texte affichage que « Type d'entrée honoraires » (`<input>` désactivé dans un `relative`). */
const U3134_READONLY_KIND_INPUT_CLASS =
  "w-full cursor-not-allowed rounded-lg border border-gray-200 bg-surface px-4 py-3 text-sm text-gray-900 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-100";

/** Même famille que projet / description désactivés : fond muted (pas `bg-surface`). */
const U3134_READONLY_KIND_INPUT_MUTED_CLASS =
  `${U3134_READONLY_KIND_INPUT_CLASS} border-gray-300 bg-muted disabled:bg-muted`;

/**
 * Fond / bordure unique pour les champs mis en évidence (honoraires) — vert fluo très clair (pastel lumineux).
 * (`[&_*]` surcharge `bg-muted` des champs read-only/disabled dans U2000.)
 */
const U3134_HONORARIUM_INPUT_SURFACE =
  "[&_input]:border-success-300! [&_input]:bg-[#dfffd6]! [&_input:disabled]:bg-[#dfffd6]! [&_div.absolute.top-0.left-0]:border-success-300! [&_div.absolute.top-0.left-0]:bg-[#dfffd6]! [&_div.absolute.top-0.left-0]:text-gray-900!";

/** Champs métier mis en évidence selon le type d'entrée honoraires (cadre seulement où le type s'applique). */
type U3134HonorariumHighlightScope = "project" | "client" | "employee" | "task" | "subproject";

/** Quels blocs surligner pour chaque type (noms alignés sur `U3134_HONORARIUM_ENTRY_KIND`). */
const U3134_HONORARIUM_ENTRY_HIGHLIGHT_SCOPES: Record<U3134HonorariumEntryKind, readonly U3134HonorariumHighlightScope[]> = {
  [U3134_HONORARIUM_ENTRY_KIND.SubProjectEmployeeTask]: ["subproject", "employee", "task"],
  [U3134_HONORARIUM_ENTRY_KIND.SubProjectEmployee]: ["subproject", "employee"],
  [U3134_HONORARIUM_ENTRY_KIND.SubProjectTask]: ["subproject", "task"],
  [U3134_HONORARIUM_ENTRY_KIND.Employee]: ["employee"],
  [U3134_HONORARIUM_ENTRY_KIND.ProjectEmployeeTask]: ["project", "employee", "task"],
  [U3134_HONORARIUM_ENTRY_KIND.ProjectEmployee]: ["project", "employee"],
  [U3134_HONORARIUM_ENTRY_KIND.ProjectTask]: ["project", "task"],
  [U3134_HONORARIUM_ENTRY_KIND.SubProject]: ["subproject"],
  [U3134_HONORARIUM_ENTRY_KIND.CustomerEmployeeTask]: ["client", "employee", "task"],
  [U3134_HONORARIUM_ENTRY_KIND.CustomerEmployee]: ["client", "employee"],
  [U3134_HONORARIUM_ENTRY_KIND.CustomerTask]: ["client", "task"],
  [U3134_HONORARIUM_ENTRY_KIND.Project]: ["project"],
  [U3134_HONORARIUM_ENTRY_KIND.Customer]: ["client"],
  [U3134_HONORARIUM_ENTRY_KIND.Partner]: [],
};

type U3134HonorariumHighlightClasses = Record<U3134HonorariumHighlightScope, string>;

const U3134_COST_AMOUNT_GRID = "grid gap-4 md:grid-cols-3 md:gap-x-3 md:items-stretch";
const U3134_COST_AMOUNT_GRID_WITH_MARKUP = "grid gap-4 md:grid-cols-4 md:gap-x-3 md:items-stretch";
const U3134_SELLING_AMOUNT_GRID_WITH_MARKUP = "grid gap-4 md:grid-cols-4 md:gap-x-3 md:items-stretch";
const U3134_SELLING_AMOUNT_GRID_NO_MARKUP = "grid gap-4 md:grid-cols-3 md:gap-x-3 md:items-stretch";
const U3134_NESTED_PANEL_CLASS = "w-full";

function U3134WorkInProgressBody({ formStateHandlers, t }: U3134WorkInProgressBodyProps) {
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const data = formStateHandlers.getData();
  const isInvoiced = isU3134InvoicedLine(data);
  /** TEC : seule Facturable suit l’état formulaire (EDIT) ; tout le reste est toujours non modifiable. */
  const fieldDisabled = true;

  const invoiceNumberDisplay = useMemo(() => {
    return u3125FormatInvoiceNumberDisplay(data?.invoice?.invoiceNumber, t("lblInvoiceNumberDraft", { ns: NS }));
  }, [data?.invoice?.invoiceNumber, t]);

  const honorariumEntryKindDisplay = useMemo(() => {
    const k = data?.honorariumEntryKind;
    if (!k) return "";
    return t(`lblHonorariumEntryKind_${k}`, { ns: NS, defaultValue: k });
  }, [data?.honorariumEntryKind, t]);

  /** Projet avec sous-projets (find) ou ligne déjà chargée (GET : `project` n’a pas toujours `includeSubProjet`). */
  const showSubprojectRow =
    data?.project?.includeSubProjet === true || Boolean(data?.subproject?.id?.trim());

  const taskDisplay = useMemo(() => {
    const tid = data?.taskId?.trim();
    if (!tid) return "";
    const line = [data?.taskCode, data?.taskDescription].filter(Boolean).join(" — ").trim();
    return line || tid;
  }, [data?.taskId, data?.taskCode, data?.taskDescription]);

  const expenseTypeDisplay = useMemo(() => {
    const code = data?.expenseTypeCode?.trim() ?? "";
    const desc = data?.expenseTypeDescription?.trim() ?? "";
    if (!code && !desc) return "";
    return [code, desc].filter(Boolean).join(" — ");
  }, [data?.expenseTypeCode, data?.expenseTypeDescription]);

  const equipmentTypeDisplay = useMemo(() => {
    const code = data?.equipmentTypeCode?.trim() ?? "";
    const desc = data?.equipmentTypeDescription?.trim() ?? "";
    if (!code && !desc) return "";
    return [code, desc].filter(Boolean).join(" — ");
  }, [data?.equipmentTypeCode, data?.equipmentTypeDescription]);

  const projectCodeDisplay = useMemo(() => {
    const p = data?.project;
    if (!p) return "";
    const num = typeof p.projetNumber === "string" ? p.projetNumber.trim() : "";
    return num || String(p.id ?? "").trim();
  }, [data?.project]);

  const subprojectCodeDisplay = useMemo(() => {
    const sp = data?.subproject;
    if (!sp) return "";
    const num = typeof sp.subProjetNumber === "string" ? sp.subProjetNumber.trim() : "";
    return num || String(sp.id ?? "").trim();
  }, [data?.subproject]);

  const billingModeDisplay = useMemo(() => {
    if (data?.hourly === true) return t("lblBillingModeHourly", { ns: NS });
    if (data?.hourly === false) return t("lblBillingModeFixedPrice", { ns: NS });
    return "";
  }, [data?.hourly, t]);

  const projectRowGridClass = showSubprojectRow
    ? "flex flex-col gap-4 md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,2fr)] md:grid-rows-[auto_auto] md:items-start md:gap-x-3 md:gap-y-0"
    : "flex flex-col gap-4 md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)] md:grid-rows-[auto_auto] md:items-start md:gap-x-3 md:gap-y-0";

  const transactionKindValue = data?.transactionKind ?? U3134_TRANSACTION_KIND.Honorarium;

  const panelSectionTitleKey = useMemo(() => {
    switch (transactionKindValue) {
      case U3134_TRANSACTION_KIND.Expense:
        return "lblPanelExpense";
      case U3134_TRANSACTION_KIND.Equipment:
        return "lblPanelEquipment";
      default:
        return "lblPanelHonorarium";
    }
  }, [transactionKindValue]);

  const isExpenseLine = transactionKindValue === U3134_TRANSACTION_KIND.Expense;
  const isEquipmentLine = transactionKindValue === U3134_TRANSACTION_KIND.Equipment;
  const isHonorariumLine = transactionKindValue === U3134_TRANSACTION_KIND.Honorarium;
  const isHonorariumFixedPrice = isHonorariumLine && data != null && isU3134HonorariumFixedPriceLine(data);
  const isProjectForfaitSelling = data != null && isU3134ProjectForfaitSellingLine(data);
  /** Vendant : profil horaire uniquement (honoraires, dépenses, équipement ; forfaitaire = Coutant seul). */
  const showSellingPanel = data?.hourly === true;
  const showMarkupInCoutant = isHonorariumLine;
  const showMarkupInVendant = showSellingPanel && (isExpenseLine || isEquipmentLine);
  const costAmountGridClass = showMarkupInCoutant
    ? U3134_COST_AMOUNT_GRID_WITH_MARKUP
    : U3134_COST_AMOUNT_GRID;
  const sellingAmountGridClass = showMarkupInVendant
    ? U3134_SELLING_AMOUNT_GRID_WITH_MARKUP
    : U3134_SELLING_AMOUNT_GRID_NO_MARKUP;
  const costRateFieldName = isExpenseLine ? "unitBaseAmount" : "costRate";
  const costRateFieldReadonly = (isHonorariumLine && !isHonorariumFixedPrice) || fieldDisabled;
  const costRateFieldRequired = isHonorariumFixedPrice;
  const sellingRateFieldName = isHonorariumLine ? "sellingRate" : "sellingUnitRate";
  const sellingRateFieldReadonly = !isHonorariumLine || fieldDisabled || isProjectForfaitSelling;
  const sellingAmountReadonly = isProjectForfaitSelling || fieldDisabled;
  const amountFieldReadonly = fieldDisabled;

  const transactionRowGridClass = isHonorariumLine
    ? "flex flex-col gap-4 md:col-span-2 md:grid md:grid-cols-[auto_11.75rem_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] md:grid-rows-[auto_auto] md:items-start md:gap-x-6 md:gap-y-0"
    : "flex flex-col gap-4 md:col-span-2 md:grid md:grid-cols-[auto_11.75rem_minmax(0,1fr)_minmax(0,1fr)] md:grid-rows-[auto_auto] md:items-start md:gap-x-6 md:gap-y-0";

  /** Fond / bordure teintée du champ read-only « type d'entrée honoraires » (même surface que les champs mis en évidence). */
  const honorariumEntryKindFieldTint = useMemo(() => {
    if (transactionKindValue !== U3134_TRANSACTION_KIND.Honorarium) return "";
    if (!data?.honorariumEntryKind) return "";
    return U3134_HONORARIUM_INPUT_SURFACE;
  }, [transactionKindValue, data?.honorariumEntryKind]);

  const honorariumHighlightClasses = useMemo((): U3134HonorariumHighlightClasses => {
    const empty: U3134HonorariumHighlightClasses = {
      project: "",
      client: "",
      employee: "",
      task: "",
      subproject: "",
    };
    if (transactionKindValue !== U3134_TRANSACTION_KIND.Honorarium) return empty;
    const k = data?.honorariumEntryKind;
    if (!k) return empty;
    const scopes = U3134_HONORARIUM_ENTRY_HIGHLIGHT_SCOPES[k] ?? [];
    if (!scopes.length) return empty;
    const surface = U3134_HONORARIUM_INPUT_SURFACE;
    const pick = (scope: U3134HonorariumHighlightScope): string =>
      scopes.includes(scope) ? surface : "";
    return {
      project: pick("project"),
      client: pick("client"),
      employee: pick("employee"),
      task: pick("task"),
      subproject: pick("subproject"),
    };
  }, [transactionKindValue, data?.honorariumEntryKind]);

  /** Projet : fond type « disabled » (muted) sauf quand le surlignage honoraires cible le projet (`bg-surface` laissée au wrapper vert). */
  const projectReadonlyInputClassName = useMemo(() => {
    if (honorariumHighlightClasses.project) return U3134_READONLY_KIND_INPUT_CLASS;
    return U3134_READONLY_KIND_INPUT_MUTED_CLASS;
  }, [honorariumHighlightClasses.project]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:gap-4">
        <div className={projectRowGridClass}>
          <div className="hidden md:block md:col-span-1 md:col-start-1 md:row-start-1 md:min-w-0">
            <U2000Label htmlFor="project" label={t("lblProject", { ns: NS })} />
          </div>
          <div className="hidden md:block md:col-span-1 md:col-start-2 md:row-start-1 md:min-w-0">
            <U2000Label htmlFor="projectDescription" label={t("lblProjectDescription", { ns: NS })} />
          </div>
          <div className="hidden md:block md:col-span-1 md:col-start-3 md:row-start-1 md:min-w-0">
            <U2000Label htmlFor="billingModeDisplay" label={t("lblBillingMode", { ns: NS })} />
          </div>
          <div className="hidden md:block md:col-span-1 md:col-start-4 md:row-start-1 md:min-w-0">
            <U2000Label htmlFor="invoiceNumberDisplay" label={t("lblInvoiceNumber", { ns: NS })} />
          </div>
          {showSubprojectRow ? (
            <>
              <div className="hidden md:block md:col-span-1 md:col-start-5 md:row-start-1 md:min-w-0">
                <U2000Label htmlFor="subproject" label={t("lblSubproject", { ns: NS })} />
              </div>
              <div className="hidden md:block md:col-span-1 md:col-start-6 md:row-start-1 md:min-w-0">
                <U2000Label htmlFor="subprojectDescription" label={t("lblSubprojectDescription", { ns: NS })} />
              </div>
            </>
          ) : null}

          <div
            className={`min-w-0 md:col-span-1 md:col-start-1 md:row-start-2 md:min-w-0 ${U3134_BODY_LABEL_FIELD_MOBILE_GAP_MD_COMPACT} ${honorariumHighlightClasses.project}`}
          >
            <div className="md:hidden">
              <U2000Label htmlFor="project" label={t("lblProject", { ns: NS })} />
            </div>
            <div className="relative">
              <input
                id="project"
                type="text"
                className={projectReadonlyInputClassName}
                value={projectCodeDisplay}
                readOnly
                disabled
              />
            </div>
          </div>
          <div
            className={`min-w-0 md:col-span-1 md:col-start-2 md:row-start-2 md:min-w-0 ${U3134_BODY_LABEL_FIELD_MOBILE_GAP_MD_COMPACT}`}
          >
            <div className="md:hidden">
              <U2000Label htmlFor="projectDescription" label={t("lblProjectDescription", { ns: NS })} />
            </div>
            <input
              id="projectDescription"
              type="text"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm disabled:cursor-not-allowed disabled:bg-muted"
              value={data?.project?.projetName ?? ""}
              disabled
              readOnly
            />
          </div>
          <div
            className={`min-w-0 md:col-span-1 md:col-start-3 md:row-start-2 md:min-w-0 ${U3134_BODY_LABEL_FIELD_MOBILE_GAP_MD_COMPACT}`}
          >
            <div className="md:hidden">
              <U2000Label htmlFor="billingModeDisplay" label={t("lblBillingMode", { ns: NS })} />
            </div>
            <input
              id="billingModeDisplay"
              type="text"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm disabled:cursor-not-allowed disabled:bg-muted"
              value={billingModeDisplay}
              disabled
              readOnly
              aria-readonly="true"
            />
          </div>
          <div
            className={`min-w-0 md:col-span-1 md:col-start-4 md:row-start-2 md:min-w-0 ${U3134_BODY_LABEL_FIELD_MOBILE_GAP_MD_COMPACT}`}
          >
            <div className="md:hidden">
              <U2000Label htmlFor="invoiceNumberDisplay" label={t("lblInvoiceNumber", { ns: NS })} />
            </div>
            <input
              id="invoiceNumberDisplay"
              type="text"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm disabled:cursor-not-allowed disabled:bg-muted"
              value={invoiceNumberDisplay}
              disabled
              readOnly
              aria-readonly="true"
            />
          </div>
          {showSubprojectRow ? (
            <>
              <div
                className={`min-h-0 min-w-0 md:col-span-1 md:col-start-5 md:row-start-2 md:min-w-0 md:self-start ${U3134_BODY_LABEL_FIELD_MOBILE_GAP_MD_COMPACT} ${honorariumHighlightClasses.subproject}`}
              >
                <div className="md:hidden">
                  <U2000Label htmlFor="subproject" label={t("lblSubproject", { ns: NS })} />
                </div>
                <div className="relative">
                  <input
                    id="subproject"
                    type="text"
                    className={U3134_READONLY_KIND_INPUT_CLASS}
                    value={subprojectCodeDisplay}
                    readOnly
                    disabled
                  />
                </div>
              </div>
              <div
                className={`min-h-0 min-w-0 md:col-span-1 md:col-start-6 md:row-start-2 md:min-w-0 md:self-start ${U3134_BODY_LABEL_FIELD_MOBILE_GAP_MD_COMPACT}`}
              >
                <div className="md:hidden">
                  <U2000Label htmlFor="subprojectDescription" label={t("lblSubprojectDescription", { ns: NS })} />
                </div>
                <input
                  id="subprojectDescription"
                  type="text"
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm disabled:cursor-not-allowed disabled:bg-muted"
                  value={data?.subproject?.subProjetName ?? ""}
                  disabled
                  readOnly
                />
              </div>
            </>
          ) : null}
        </div>
        <div className={`min-w-0 ${honorariumHighlightClasses.client}`}>
          <U2000InputText formStateHandlers={formStateHandlers as any} fieldName="clientDisplay" label={t("lblClient", { ns: NS })} readonly required={false} />
        </div>
      </div>

      <U2000Panel title={t(panelSectionTitleKey, { ns: NS })}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Desktop : L1 = libellés, L2 = Facturable + champs ; pas de min-h sur col1 L1 pour éviter d’étirer la ligne et un « trou » géant sous les libellés (comme la grille honoraires + `space-y-1.5`). */}
          <div className={transactionRowGridClass}>
            <div
              className="hidden md:block md:col-span-1 md:col-start-1 md:row-start-1 md:h-0 md:min-h-0"
              aria-hidden
            />
            <div className="hidden md:block md:col-span-1 md:col-start-2 md:row-start-1">
              <U2000Label htmlFor="transactionDate" label={t("lblTransactionDate", { ns: NS })} />
            </div>
            <div className="hidden md:block md:col-span-1 md:col-start-3 md:row-start-1 md:min-w-0">
              <U2000Label htmlFor="employeeDisplay" label={t("lblEmployee", { ns: NS })} />
            </div>
            <div className="hidden md:block md:col-span-1 md:col-start-4 md:row-start-1 md:min-w-0">
              <U2000Label
                htmlFor={
                  isExpenseLine ? "expenseTypeDisplay" : isEquipmentLine ? "equipmentTypeDisplay" : "taskId"
                }
                label={
                  isExpenseLine
                    ? t("lblExpenseType", { ns: NS })
                    : isEquipmentLine
                      ? t("lblEquipmentType", { ns: NS })
                      : t("lblTask", { ns: NS })
                }
              />
            </div>
            {isHonorariumLine ? (
              <div className="hidden md:block md:col-span-1 md:col-start-5 md:row-start-1 md:min-w-0">
                <U2000Label htmlFor="honorariumEntryKind" label={t("lblHonorariumEntryKind", { ns: NS })} />
              </div>
            ) : null}
            <div className="flex items-center md:col-span-1 md:col-start-1 md:row-start-2 md:min-h-[46px]">
              <U2000CheckBox formStateHandlers={formStateHandlers as any} fieldName="billable" label={t("lblBillable", { ns: NS })} readonly={readonly || isInvoiced} />
            </div>
            <div className={`min-h-0 w-full ${U3134_BODY_LABEL_FIELD_MOBILE_GAP_MD_COMPACT} md:col-span-1 md:col-start-2 md:row-start-2 md:w-auto`}>
              <div className="md:hidden">
                <U2000Label htmlFor="transactionDate" label={t("lblTransactionDate", { ns: NS })} />
              </div>
              <div className="w-full max-w-47">
                <U2000InputCalendarCustom
                  formStateHandlers={formStateHandlers as any}
                  fieldName="transactionDate"
                  required
                  readonly={fieldDisabled}
                  dateFormat="yyyy-MM-dd"
                  popupPosition="bottom"
                />
              </div>
            </div>
            <div
              className={`min-h-0 min-w-0 ${U3134_BODY_LABEL_FIELD_MOBILE_GAP_MD_COMPACT} md:col-span-1 md:col-start-3 md:row-start-2 md:min-w-0 md:self-start ${honorariumHighlightClasses.employee}`}
            >
              <div className="md:hidden">
                <U2000Label htmlFor="employeeDisplay" label={t("lblEmployee", { ns: NS })} />
              </div>
              <U2000InputText
                formStateHandlers={formStateHandlers as any}
                fieldName="employeeDisplay"
                label={t("lblEmployee", { ns: NS })}
                hideLabel
                readonly={fieldDisabled}
                required={false}
              />
            </div>
            <div
              className={`min-h-0 min-w-0 ${U3134_BODY_LABEL_FIELD_MOBILE_GAP_MD_COMPACT} md:col-span-1 md:col-start-4 md:row-start-2 md:min-w-0 md:self-start ${isExpenseLine || isEquipmentLine ? "" : honorariumHighlightClasses.task}`}
            >
              {isExpenseLine ? (
                <>
                  <div className="md:hidden">
                    <U2000Label htmlFor="expenseTypeDisplay" label={t("lblExpenseType", { ns: NS })} />
                  </div>
                  <div className="relative">
                    <input
                      id="expenseTypeDisplay"
                      type="text"
                      className={U3134_READONLY_KIND_INPUT_CLASS}
                      value={expenseTypeDisplay}
                      readOnly
                      disabled
                      aria-readonly="true"
                    />
                  </div>
                </>
              ) : isEquipmentLine ? (
                <>
                  <div className="md:hidden">
                    <U2000Label htmlFor="equipmentTypeDisplay" label={t("lblEquipmentType", { ns: NS })} />
                  </div>
                  <div className="relative">
                    <input
                      id="equipmentTypeDisplay"
                      type="text"
                      className={U3134_READONLY_KIND_INPUT_MUTED_CLASS}
                      value={equipmentTypeDisplay}
                      readOnly
                      disabled
                      aria-readonly="true"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="md:hidden">
                    <U2000Label htmlFor="taskId" label={t("lblTask", { ns: NS })} />
                  </div>
                  <input
                    id="taskId"
                    type="text"
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm disabled:cursor-not-allowed disabled:bg-muted"
                    value={taskDisplay}
                    readOnly
                    disabled
                  />
                </>
              )}
            </div>
            {isHonorariumLine ? (
              <div
                className={`min-h-0 min-w-0 ${U3134_BODY_LABEL_FIELD_MOBILE_GAP_MD_COMPACT} md:col-span-1 md:col-start-5 md:row-start-2 md:min-w-0 md:self-start ${honorariumEntryKindFieldTint}`}
              >
                <div className="md:hidden">
                  <U2000Label htmlFor="honorariumEntryKind" label={t("lblHonorariumEntryKind", { ns: NS })} />
                </div>
                <div className="relative">
                  <input
                    id="honorariumEntryKind"
                    type="text"
                    className={U3134_READONLY_KIND_INPUT_CLASS}
                    value={honorariumEntryKindDisplay}
                    readOnly
                    disabled
                  />
                </div>
              </div>
            ) : null}
          </div>

          {(isHonorariumLine || isExpenseLine || isEquipmentLine) ? (
            <div className="flex flex-col gap-6 md:col-span-2">
              <U2000Panel
                title={t("lblPanelCost", { ns: NS })}
                variant="outlined"
                className={U3134_NESTED_PANEL_CLASS}
              >
                <div className={costAmountGridClass}>
                  <div className="min-w-0">
                    <U2000InputDecimal
                      formStateHandlers={formStateHandlers as any}
                      fieldName="quantity"
                      label={t("lblQuantity", { ns: NS })}
                      required
                      decimalDigits={2}
                      allowNegative={false}
                      readonly={amountFieldReadonly}
                    />
                  </div>
                  <div className="min-w-0">
                    <U2000InputCurrency
                      formStateHandlers={formStateHandlers as any}
                      fieldName={costRateFieldName}
                      label={t("lblCostRate", { ns: NS })}
                      required={costRateFieldRequired}
                      readonly={costRateFieldReadonly}
                      disabled={fieldDisabled || costRateFieldReadonly}
                      decimalDigits={2}
                      allowNegative={false}
                    />
                  </div>
                  {showMarkupInCoutant ? (
                    <div className="min-w-0">
                      <U2000InputDecimal
                        formStateHandlers={formStateHandlers as any}
                        fieldName="costMarkupFactor"
                        label={t("lblMarkupFactor", { ns: NS })}
                        required
                        decimalDigits={2}
                        allowNegative={false}
                        readonly={amountFieldReadonly}
                      />
                    </div>
                  ) : null}
                  <div className="min-w-0">
                    <U2000InputCurrency
                      formStateHandlers={formStateHandlers as any}
                      fieldName="costAmount"
                      label={t("lblCostAmount", { ns: NS })}
                      required
                      decimalDigits={2}
                      allowNegative={false}
                      readonly={amountFieldReadonly}
                      disabled={fieldDisabled}
                    />
                  </div>
                </div>
              </U2000Panel>

              {showSellingPanel ? (
                <U2000Panel
                  title={t("lblPanelSelling", { ns: NS })}
                  variant="outlined"
                  className={U3134_NESTED_PANEL_CLASS}
                >
                  <div className={sellingAmountGridClass}>
                    <div className="min-w-0">
                      <U2000InputDecimal
                        formStateHandlers={formStateHandlers as any}
                        fieldName="quantity"
                        label={t("lblQuantity", { ns: NS })}
                        required
                        decimalDigits={2}
                        allowNegative={false}
                        readonly={amountFieldReadonly}
                      />
                    </div>
                    <div className="min-w-0">
                      <U2000InputCurrency
                        formStateHandlers={formStateHandlers as any}
                        fieldName={sellingRateFieldName}
                        label={t("lblSellingRate", { ns: NS })}
                        required={isHonorariumLine}
                        readonly={sellingRateFieldReadonly}
                        disabled={fieldDisabled || sellingRateFieldReadonly}
                        decimalDigits={2}
                        allowNegative={false}
                      />
                    </div>
                    {showMarkupInVendant ? (
                      <div className="min-w-0">
                        <U2000InputDecimal
                          formStateHandlers={formStateHandlers as any}
                          fieldName="sellingMarkupFactor"
                          label={t("lblMarkupFactor", { ns: NS })}
                          required
                          decimalDigits={2}
                          allowNegative={false}
                          readonly={amountFieldReadonly}
                        />
                      </div>
                    ) : null}
                    <div className="min-w-0">
                      <U2000InputCurrency
                        formStateHandlers={formStateHandlers as any}
                        fieldName="amount"
                        label={t("lblSellingAmount", { ns: NS })}
                        required
                        readonly={sellingAmountReadonly}
                        disabled={fieldDisabled || sellingAmountReadonly}
                        decimalDigits={2}
                        allowNegative
                      />
                    </div>
                  </div>
                </U2000Panel>
              ) : null}
            </div>
          ) : null}

          <div className={`min-w-0 ${U3134_BODY_LABEL_FIELD_GAP} md:col-span-2`}>
            <U2000Label htmlFor="remark" label={t("lblRemark", { ns: NS })} />
            <U2000InputText formStateHandlers={formStateHandlers as any} fieldName="remark" label="" hideLabel required={false} readonly={fieldDisabled} />
            <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName="remark" />
          </div>
        </div>
      </U2000Panel>
    </div>
  );
}

export default U3134WorkInProgressBody;
